/**
 * Closed-form + trace-state grading primitives. All pure, all integer per-mille, so
 * the same inputs grade identically in the browser and the Worker. Drill runners
 * (Phase 5) call these; the executable path (run code/counterexample) is graded by the
 * judge and fed in as a boolean pass/fail (gradeExecutableResult).
 */

import { stableStringify } from './determinism.js';
import { PM, type GradeResult, type PerMille, type MisconceptionTag } from './models.js';

function statusFor(score: PerMille): GradeResult['status'] {
  if (score >= PM.FULL) return 'correct';
  if (score <= PM.ZERO) return 'incorrect';
  return 'partially_correct';
}

function result(
  score: PerMille,
  opts: {
    correct?: readonly string[];
    missing?: readonly string[];
    misconceptions?: readonly MisconceptionTag[];
  } = {},
): GradeResult {
  return {
    status: statusFor(score),
    score,
    correctComponents: opts.correct ?? [],
    missingComponents: opts.missing ?? [],
    misconceptionTags: opts.misconceptions ?? [],
  };
}

/** Single-answer select (MCQ). A wrong pick surfaces its distractor's misconception. */
export function gradeSingleSelect(input: {
  correctId: string;
  acceptedIds?: readonly string[];
  responseId: string;
  distractors?: ReadonlyArray<{ id: string; misconception: MisconceptionTag }>;
}): GradeResult {
  const accepted = new Set([input.correctId, ...(input.acceptedIds ?? [])]);
  if (accepted.has(input.responseId)) return result(PM.FULL, { correct: [input.responseId] });
  const d = input.distractors?.find((x) => x.id === input.responseId);
  return result(PM.ZERO, {
    missing: [input.correctId],
    misconceptions: d ? [d.misconception] : [],
  });
}

/** Set-equality grading with Jaccard partial credit (multi-select, span tagging). */
export function gradeSet(input: {
  correctIds: readonly string[];
  responseIds: readonly string[];
}): GradeResult {
  const correct = new Set(input.correctIds);
  const response = new Set(input.responseIds);
  let tp = 0;
  for (const id of response) if (correct.has(id)) tp++;
  const fp = response.size - tp;
  const fn = correct.size - tp;
  const denom = tp + fp + fn;
  const score = denom === 0 ? PM.FULL : PM.clamp((1000 * tp) / denom);
  const missing = [...correct].filter((id) => !response.has(id));
  const extra = [...response].filter((id) => !correct.has(id));
  return result(score, {
    correct: [...response].filter((id) => correct.has(id)),
    missing: [...missing, ...extra.map((e) => `extra:${e}`)],
  });
}

/** Ordering: fraction of items in their correct absolute position. */
export function gradeOrdering(input: {
  correctOrder: readonly string[];
  responseOrder: readonly string[];
}): GradeResult {
  const n = input.correctOrder.length;
  if (n === 0) return result(PM.FULL);
  let matches = 0;
  for (let i = 0; i < n; i++) {
    if (input.responseOrder[i] === input.correctOrder[i]) matches++;
  }
  return result(PM.clamp((1000 * matches) / n));
}

/** Numeric answer within an inclusive integer tolerance. */
export function gradeNumeric(input: {
  expected: number;
  response: number;
  tolerance?: number;
}): GradeResult {
  const tol = input.tolerance ?? 0;
  const okp = Math.abs(input.expected - input.response) <= tol;
  return result(okp ? PM.FULL : PM.ZERO, okp ? {} : { missing: [String(input.expected)] });
}

/** Fill-in blanks: fraction correct after trimming. */
export function gradeFill(input: {
  expected: readonly string[];
  responses: readonly string[];
}): GradeResult {
  const n = input.expected.length;
  if (n === 0) return result(PM.FULL);
  const norm = (s: string | undefined) => (s ?? '').replace(/\s+/g, '').trim();
  let matches = 0;
  const missing: string[] = [];
  for (let i = 0; i < n; i++) {
    if (norm(input.responses[i]) === norm(input.expected[i])) matches++;
    else missing.push(input.expected[i]!);
  }
  return result(PM.clamp((1000 * matches) / n), { missing });
}

/** Trace-state prediction: exact canonical match (PRD F-06). */
export function gradeTraceState(input: { expected: unknown; response: unknown }): GradeResult {
  const okp = stableStringify(input.expected) === stableStringify(input.response);
  return result(okp ? PM.FULL : PM.ZERO);
}

/** Executable artifacts (code/counterexample) graded by the judge → pass/fail in. */
export function gradeExecutableResult(input: {
  passed: boolean;
  misconceptionOnFail?: MisconceptionTag;
}): GradeResult {
  return input.passed
    ? result(PM.FULL)
    : result(PM.ZERO, {
        misconceptions: input.misconceptionOnFail ? [input.misconceptionOnFail] : [],
      });
}
